import { useMemo, useCallback } from 'react'
import {
  GetStatusParams,
  OperationalPlanTableProps,
  PlanTableRow,
} from './OperationalPlanner.types'
import {
  CancelOutlined,
  Check,
  Pause,
  Lock as LockIcon,
  ErrorOutline as ErrorOutlineIcon,
  Publish,
} from '@mui/icons-material'
import {
  GridActionsCellItem,
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
  GridValidRowModel,
} from '@mui/x-data-grid-premium'
import { BaseTable } from 'Features/Table/BaseTable.view'
import {
  OPERTAUIONAL_PLAN_STATUS,
  PLAN_ACCESS_TYPE,
  PLAN_STATUS,
} from 'Common/Constants/Constants'
import { Tooltip, Box, CircularProgress, Typography } from '@mui/material'
import { ExtendedGridColDef } from 'Features/Table/BulkUpdateModal'

export const OperationalPlannerMUITable = ({
  data,
  handleRowClick,
  handleRenamePlan,
  handleDeletePlan,
  handleClonePlan,
  handleEditPlan,
  handleRunPlan,
  handleDownloadPlan,
  handlePlanAccessChange,
  userEmail,
  isLoading,
}: OperationalPlanTableProps) => {
  const getStatus = useCallback(
    ({ status, completed, total, error }: GetStatusParams) => {
      switch (status) {
        case 'SUCCESS':
        case OPERTAUIONAL_PLAN_STATUS.PUBLISHED:
          return (
            <Box
              display="flex"
              justifyContent="space-between"
              width="100%"
              height="100%"
              alignItems="center"
            >
              <Typography
                variant={'button'}
                style={{ color: 'green', fontWeight: 'bold' }}
              >
                {status} ({completed} / {total})
              </Typography>
              <Check style={{ color: 'green', fontWeight: 'bold' }} />
            </Box>
          )
        case 'RUNNING':
          return (
            <Box
              display="flex"
              justifyContent="space-between"
              width="100%"
              height="100%"
              alignItems="center"
            >
              <Typography
                variant={'button'}
                style={{ color: 'orange', fontWeight: 'bold' }}
              >
                {status}... ({completed} / {total})
              </Typography>
              <CircularProgress
                sx={{ color: 'orange', fontWeight: 'bold' }}
                size={18}
              />
            </Box>
          )
        case 'FAILED':
          // WP6 6.7: the SNP job writes the failure message into the row's
          // `error`; surface it on hover (the API list already returns it).
          return (
            <Tooltip title={error || ''} placement="top">
              <Box
                display="flex"
                justifyContent="space-between"
                width="100%"
                height="100%"
                alignItems="center"
              >
                <Typography
                  variant={'button'}
                  style={{ color: 'red', fontWeight: 'bold' }}
                >
                  {status}
                </Typography>
                <CancelOutlined style={{ color: 'red', fontWeight: 'bold' }} />
              </Box>
            </Tooltip>
          )
        case 'INITIATED':
          return (
            <Box
              display="flex"
              justifyContent="space-between"
              width="100%"
              height="100%"
              alignItems="center"
            >
              <Typography
                variant={'button'}
                style={{ color: 'orange', fontWeight: 'bold' }}
              >
                {status}...
              </Typography>
              <CircularProgress
                sx={{ color: 'orange', fontWeight: 'bold' }}
                size={18}
              />
            </Box>
          )
        case 'DELETING':
          return (
            <Box
              display="flex"
              justifyContent="space-between"
              width="100%"
              height="100%"
              alignItems="center"
            >
              <Typography
                variant={'button'}
                style={{ color: 'lightgrey', fontWeight: 'bold' }}
              >
                {status}...
              </Typography>
              <CircularProgress
                sx={{ color: 'lightgrey', fontWeight: 'bold' }}
                size={18}
              />
            </Box>
          )
        case 'BEING_CLONED':
          return (
            <Box
              display="flex"
              justifyContent="space-between"
              width="100%"
              height="100%"
              alignItems="center"
            >
              <Typography
                variant={'button'}
                style={{ color: 'lightgrey', fontWeight: 'bold' }}
              >
                BEING CLONED ...
              </Typography>
              <CircularProgress
                sx={{ color: 'lightgrey', fontWeight: 'bold' }}
                size={18}
              />
            </Box>
          )
        case 'CLONING':
          return (
            <Box
              display="flex"
              justifyContent="space-between"
              width="100%"
              height="100%"
              alignItems="center"
            >
              <Typography
                variant={'button'}
                style={{ color: 'lightgrey', fontWeight: 'bold' }}
              >
                CLONING ...
              </Typography>
              <CircularProgress
                sx={{ color: 'lightgrey', fontWeight: 'bold' }}
                size={18}
              />
            </Box>
          )
        case 'PLAN_DATA_LOADED':
          return (
            <Box
              display="flex"
              justifyContent="space-between"
              width="100%"
              height="100%"
              alignItems="center"
            >
              <Typography
                variant={'button'}
                style={{ color: 'green', fontWeight: 'bold' }}
              >
                PLAN DATA LOADED
              </Typography>
              <Pause style={{ color: 'green', fontWeight: 'bold' }} />
            </Box>
          )
      }
      return (
        <Box
          display="flex"
          justifyContent="space-between"
          width="100%"
          height="100%"
          alignItems="center"
        >
          <Typography
            variant={'button'}
            style={{ color: 'orange', fontWeight: 'bold' }}
          >
            {status}
          </Typography>
        </Box>
      )
    },
    [data]
  )

  const getMenuItems = useCallback(
    (row: PlanTableRow) => {
      let menuItems: JSX.Element[] = []
      const {
        planId,
        planName,
        status,
        planGeneratedBy,
        planAccessType,
        planPublished,
      } = row
      let lockPlanOption: JSX.Element | null = null
      if (planGeneratedBy === userEmail) {
        if (planAccessType.planAccessType === PLAN_ACCESS_TYPE.PUBLIC) {
          lockPlanOption = (
            <GridActionsCellItem
              key={'&100-element'}
              label="Make private"
              onClick={() =>
                handlePlanAccessChange(planId, {
                  ...planAccessType,
                  planAccessType: PLAN_ACCESS_TYPE.PRIVATE,
                })
              }
              showInMenu
            />
          )
        } else if (planAccessType.planAccessType === PLAN_ACCESS_TYPE.PRIVATE) {
          lockPlanOption = (
            <GridActionsCellItem
              key={'&101-element'}
              label="Make public"
              onClick={() =>
                handlePlanAccessChange(planId, {
                  ...planAccessType,
                  planAccessType: PLAN_ACCESS_TYPE.PUBLIC,
                })
              }
              showInMenu
            />
          )
        }
      }

      let editabilityOption: JSX.Element | null = null

      if (planGeneratedBy === userEmail) {
        if (planAccessType.readonly === false) {
          editabilityOption = (
            <GridActionsCellItem
              key={'&110-element'}
              label="Make read only"
              onClick={() =>
                handlePlanAccessChange(planId, {
                  ...planAccessType,
                  readonly: true,
                })
              }
              showInMenu
            />
          )
        } else if (planAccessType.readonly === true) {
          editabilityOption = (
            <GridActionsCellItem
              key={'&111-element'}
              label="Undo read only"
              disabled={planPublished}
              onClick={() =>
                handlePlanAccessChange(planId, {
                  ...planAccessType,
                  readonly: false,
                })
              }
              showInMenu
            />
          )
        }
      }

      switch (status) {
        case PLAN_STATUS.SUCCESS:
        case OPERTAUIONAL_PLAN_STATUS.PUBLISHED:
          menuItems = [
            ...(planAccessType.readonly === true
              ? []
              : [
                  <GridActionsCellItem
                    key={'&1-element'}
                    label="Rename"
                    onClick={() => handleRenamePlan(planId, planName, status)}
                    showInMenu
                  />,
                ]),
            <GridActionsCellItem
              key={'&2-element'}
              label="Clone"
              onClick={() => handleClonePlan(planId, planName, status)}
              showInMenu
            />,
            ...(planAccessType.readonly === true
              ? []
              : [
                  <GridActionsCellItem
                    key={'&3-element'}
                    label="Delete"
                    onClick={() => handleDeletePlan(planId, planName, status)}
                    showInMenu
                  />,
                ]),
            <GridActionsCellItem
              key={'&4-element'}
              label="Download"
              onClick={() => handleDownloadPlan(planId, planName, status)}
              showInMenu
            />,
            <GridActionsCellItem
              key={'&4-element'}
              label="Edit"
              onClick={() => handleEditPlan(planId)}
              showInMenu
            />,
          ]
          if (lockPlanOption) {
            menuItems.push(lockPlanOption)
          }

          if (editabilityOption) {
            menuItems.push(editabilityOption)
          }
          break
        case PLAN_STATUS.PLAN_DATA_LOADED:
          menuItems = [
            ...(planAccessType.readonly === true
              ? []
              : [
                  <GridActionsCellItem
                    key={'&1-element'}
                    label="Run plan"
                    onClick={() => handleRunPlan(planId)}
                    showInMenu
                  />,
                ]),
            ...(planAccessType.readonly === true
              ? []
              : [
                  <GridActionsCellItem
                    key={'&2-element'}
                    label="Rename"
                    onClick={() => handleRenamePlan(planId, planName, status)}
                    showInMenu
                  />,
                ]),
            <GridActionsCellItem
              key={'&3-element'}
              label="Edit"
              onClick={() => handleEditPlan(planId)}
              showInMenu
            />,
            ...(planAccessType.readonly === true
              ? []
              : [
                  <GridActionsCellItem
                    key={'&4-element'}
                    label="Delete"
                    onClick={() => handleDeletePlan(planId, planName, status)}
                    showInMenu
                  />,
                ]),
          ]
          break

        case PLAN_STATUS.RUNNING:
          menuItems = [
            ...(planAccessType.readonly === true
              ? []
              : [
                  <GridActionsCellItem
                    key={'&1-element'}
                    label="Rename"
                    onClick={() => handleRenamePlan(planId, planName, status)}
                    showInMenu
                  />,
                ]),
            ...(planAccessType.readonly === true
              ? []
              : [
                  <GridActionsCellItem
                    key={'&3-element'}
                    label="Delete"
                    onClick={() => handleDeletePlan(planId, planName, status)}
                    showInMenu
                  />,
                ]),
          ]
          break
        case PLAN_STATUS.FAILED:
          menuItems = [
            ...(planAccessType.readonly === true
              ? []
              : [
                  <GridActionsCellItem
                    key={'&3-element'}
                    label="Delete"
                    onClick={() => handleDeletePlan(planId, planName, status)}
                    showInMenu
                  />,
                ]),
          ]
          if (lockPlanOption) {
            menuItems.push(lockPlanOption)
          }
          break
        default:
          menuItems = []
      }
      return menuItems
    },
    [data]
  )

  const getRowId = useCallback(
    (row: GridValidRowModel) => {
      return row.runId ?? row.planId
    },
    [data]
  )
  const columns: ExtendedGridColDef[] = useMemo(
    () => [
      {
        headerName: 'Plan name',
        field: 'planName',
        flex: 1,
        renderCell: (params: GridRenderCellParams<PlanTableRow, string>) => {
          const row: PlanTableRow = params.row
          return (
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
              minWidth="100%"
              minHeight="100%"
              alignItems="center"
              gap={1}
            >
              <Box display="flex">
                <Typography>{row?.planName}</Typography>
              </Box>
              <Box
                display="flex"
                sx={{
                  width: 'fit-content',
                }}
              >
                {row?.planAccessType?.planAccessType ===
                  PLAN_ACCESS_TYPE.PRIVATE && (
                  <Box>
                    <Tooltip title="This plan is locked.">
                      <LockIcon fontSize="small" />
                    </Tooltip>
                  </Box>
                )}
                {row?.planAccessType?.readonly === true && (
                  <Box>
                    <Tooltip title="This plan is read only.">
                      <ErrorOutlineIcon fontSize="small" />
                    </Tooltip>
                  </Box>
                )}
                {row?.planPublished && (
                  <Box>
                    <Tooltip title="This plan is published.">
                      <Publish fontSize="small" color="secondary" />
                    </Tooltip>
                  </Box>
                )}
              </Box>
            </Box>
          )
        },
      },
      {
        headerName: 'Plan start week',
        field: 'planStartWeek',
        flex: 1,
        valueGetter: (params: string) => {
          const dateString = params
          // 2025-08-25 -> 25/08/2025
          if (!dateString || !/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
            return 'N/A'
          }
          const [year, month, day] = dateString?.split('-')
          return `${day}/${month}/${year}`
        },
      },
      {
        headerName: 'Status',
        field: 'status',
        renderCell: (params: GridRenderCellParams<PlanTableRow, string>) => {
          const row: PlanTableRow = params.row
          let completed = 0
          let total = 0
          if (row.status_detail) {
            const { completed: _completed, total: _total } = row.status_detail
            completed = _completed
            total = _total
          }
          const { status, error } = row
          return getStatus({ status, completed, total, error })
        },
        flex: 1,
      },
      {
        headerName: 'Plan generated on',
        field: 'planGeneratedOn',
        flex: 1,
      },
      {
        headerName: 'Plan generated by',
        field: 'planGeneratedBy',
        flex: 1,
      },
      {
        headerName: 'Actions',
        field: 'actions',
        type: 'actions',
        width: 80,
        getActions: (params: GridRowParams<PlanTableRow>) => {
          return getMenuItems(params.row)
        },
      },
    ],
    [data]
  )

  // No-op functions for BaseTable required props (read-only table)
  const updateTableData = () => {
    // This is a read-only table, no updates needed
  }

  const uploadData = () => {
    // No upload functionality needed
  }

  const handleRowClickWrapper = (params: any, event: any, details: any) => {
    handleRowClick(params.row)
  }

  return (
    <Box
      sx={{
        display: 'flex',
        overflow: 'auto',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
      }}
    >
      <BaseTable
        data={data ?? []}
        columns={columns}
        isLoading={isLoading}
        updateTableData={updateTableData}
        uploadData={uploadData}
        isUploading={false}
        isSaving={false}
        checkBoxSelection={false}
        showTableToolBar={false}
        handleRowClick={handleRowClickWrapper}
        pinnedColumns={['planName']}
        downloadFileName="OperationalPlans"
        density="standard"
      />
    </Box>
  )
}
